/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import Handsontable from 'handsontable';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.min.css';
import {
  NUMERIC_KEYS,
  RENDERING_AS_COL,
} from '../constants';
import { registerAllModules } from 'handsontable/registry';
import '../styles.css';
import { useBoolean } from '@chakra-ui/react';
import { useContext } from 'react';
import { AuditSheetContext } from '../../../providers/AuditSheetProvider';
import { useInterval } from '../../../hooks';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { getRowColArray, getRowColIndex } from '../getRowColumn';
import AppColors from '../../../constants/AppColors';
import { registerCustomCells } from './hands_on_table_renderers/CustomRenderers';
import { format } from 'date-fns';
import { toTitleCase } from '../../../utils/common.utils';
import { prepareAuditSheetHeader } from '../utils/prepareAuditSheetHeader';
import { prepareAuditSheetColumn } from '../utils/prepareAuditSheetColumn';
import { validateUnits, handleAgreeDisagree, updateICDAudit } from '../utils/preparePayload';
import {
  findAgreeDisAgreeColumn,
  findDollorColumn,
  findHighlightedColumn,
  findNumericColumns,
  findResponseColumnIndex,
  getAuditSheetKeys,
} from '../utils/findColumnProps';
import { validateColumnValue } from '../utils/auditsheetValidation';
import { decodeResponseValue, generateOptions, MultiSelectEditor } from '../utils/responseColumn';
import { createDynamicColumnKeyToCountMap } from '../utils/commonAuditSheet.utils';

registerCustomCells({});

// Register the editor
Handsontable.editors.registerEditor('multi-select', MultiSelectEditor);

function getScaledRowHeight(scale) {
  return `${24 * scale}px`;
}

function getScaledHeaderFontSize(scale) {
  return 13 * scale + 'px';
}

function getScaledRowFontSize(scale) {
  return 14 * scale + 'px';
}

const AuditSheet = ({
  scale = 1,
  sheet,
  uploadId,
  view,
  updateAuditSheet,
  updateSheet,
  onCommentClick,
  onSelectionEnd,
  closeComment,
  height,
  removeRows,
  getSheet,
  batch,
  batchId
}) => {
  //   refs
  const hotRef = useRef(null);

  /**
   *  { updateSheet } update the data in setState(setSheet);
   *  { updateAuditSheet } create audit sheet(POST) and getSheets and update sheet;
   *  { sheet } gives a current sheet not sheets
   */

  // booleans
  const [isProviderDollarDisabled, ipddState] = useBoolean(false);
  const [isAuditedDollarDisabled, iaddState] = useBoolean(false);

  // context data
  const auditSheetContext = useContext(AuditSheetContext);
  const {
    industryCodes,
    iCDCodes,
    modifiers,
    getComments,
    commentFlags,
    providerOptions,
    currentUpload,
    responseAuditIssues,
  } = auditSheetContext;

  const responseAuditIssuesRef = useRef(responseAuditIssues);

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }

  // states

  const [sheetData, setSheetData] = useState([]);
  const [windowSize, setWindowSize] = useState(getWindowSize().innerWidth);

  const [columnData, setColumnData] = useState([]);
  const [auditSheetHeaders, setAuditSheetHeaders] = useState([]);

  const [numericColumns, setNumericColumns] = useState([]);
  const [responseColumn, setResponseColumn] = useState(null);

  const [highlightedColumn, setHilightedColumn] = React.useState([]);

  const [dollarColumn, setDollorColumn] = React.useState([]);

  const [auditSheetColumnsKeys, setAuditSheetColumnsKeys] = React.useState([]);

  const [agreeDisAgreeCol, setAgreeDisAgreeCol] = React.useState([]);

  //   other hooks
  const toast = useToastr();

  useInterval(() => {
    //call update audit sheet
    if (!view) {
      updateAuditSheet();
    }
  }, 50000);

  registerAllModules();

  //   table props
  Handsontable.renderers.registerRenderer(
    'customStylesRenderer',
    (hotInstance, TD, ...rest) => {
      Handsontable.renderers.getRenderer('text')(hotInstance, TD, ...rest);
      TD.style.fontSize = getScaledRowFontSize(scale);
      TD.style.textAlign = 'center';
    }
  );
  Handsontable.renderers.registerRenderer(
    'agreeRenderer',
    (hotInstance, TD, row, column, prop, value, cellProperties) => {
      Handsontable.renderers.getRenderer('text')(
        hotInstance,
        TD,
        row,
        column,
        prop,
        value,
        cellProperties
      );
      TD.style.fontSize = getScaledRowFontSize(scale);
      TD.style.textAlign = 'center';
      if (value != null) {
        TD.innerHTML = value === true || value === 'true' ? 'Yes' : '-';
      }
    }
  );

  function setLocalSheetData(data) {
    if (!data) {
      setSheetData([]);
    }
    const nextSheetData = [];

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];

      const renderingValue = datum.rendering;

      if (renderingValue) {
        const isObject =
          typeof renderingValue === 'object' &&
          Object.keys(renderingValue).length;

        if (isObject) {
          const renderName = toTitleCase(
            `${renderingValue.first_name} ${renderingValue.last_name}`
          );

          datum.rendering = renderName;

          datum.old_rendering = renderingValue;
        } else {
          const providerOption = providerOptions.find(
            p => p.id === renderingValue
          );
          if (providerOption) {
            datum.rendering = providerOption.value;
          }
        }
      }
      if (datum.enc_dt) {
        const d = Date.parse(datum.enc_dt);
        if (d) {
          datum.enc_dt = format(new Date(d), 'MM-dd-yyyy');
        }
      }
      nextSheetData.push(datum);
    }
    //  des-object and set to setSheetData(local)
    setSheetData(nextSheetData);
  }

  // lifecycle
  useEffect(() => {
    function handleWindowResize() {
      const size = getWindowSize();
      setWindowSize(size.innerWidth);
    }
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    registerCustomCells({ scale });
  }, [scale]);


  useEffect(() => {
    const reDestructuredData = sheet.data;

    // spreading in object
    const _reDestructuredData = reDestructuredData.map(sd => {
      const tempObj = { ...sd, ...sd?.additional_attributes };

      delete tempObj.additional_attributes;

      return tempObj;
    });

    const columnMapping = createDynamicColumnKeyToCountMap(_reDestructuredData)
    // -- preparing dynamic column headers --
    const auditSheetColumnsheaders = prepareAuditSheetHeader(
      _reDestructuredData,
      columnMapping, 
      batch
    );
    setAuditSheetHeaders(auditSheetColumnsheaders);

    // -- preparing dynamic auditsheetcolummn --
    const dynamicColumnData = prepareAuditSheetColumn(
      _reDestructuredData,
      windowSize,
      view,
      providerOptions,
      isAuditedDollarDisabled,
      isProviderDollarDisabled,
      columnMapping,
      batch
    );

    const responseColumnIndex = dynamicColumnData.findIndex(
      col => col.data === 'response'
    );

    if (responseColumnIndex !== -1) {
      dynamicColumnData[responseColumnIndex].source = function () {
        const rowData = this.instance.getSourceDataAtRow(this.row);
        return generateOptions(rowData, responseAuditIssuesRef.current);
      };

      dynamicColumnData[responseColumnIndex].renderer = function (
        instance,
        td,
        row,
        col,
        prop,
        value,
        cellProperties
      ) {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);
        const rowData = instance.getSourceDataAtRow(row);
        td.innerText = decodeResponseValue(value, rowData, responseAuditIssuesRef.current) || '';
        Object.assign(td.style, { textAlign: 'center', verticalAlign: 'middle' });
        return td;
      };
    }

    if (!!dynamicColumnData.length) {
      setColumnData(dynamicColumnData);
    }

    if (sheet?.data) {
      setLocalSheetData(sheet?.data);
    }
  }, [sheet?.data, responseAuditIssues]);


  React.useEffect(() => {
    const _hilghtedColumn = findHighlightedColumn(columnData);
    const _dollorColumn = findDollorColumn(columnData);
    const _auditSheetkey = getAuditSheetKeys(columnData);
    const _agreeDisagree = findAgreeDisAgreeColumn(columnData);
    const _numericColumn = findNumericColumns(columnData);
    const _responseColumn = findResponseColumnIndex(columnData);

    setResponseColumn(_responseColumn);
    setAuditSheetColumnsKeys([..._auditSheetkey]);
    setHilightedColumn([..._hilghtedColumn]);
    setDollorColumn([..._dollorColumn]);
    setAgreeDisAgreeCol([..._agreeDisagree]);
    setNumericColumns(_numericColumn);
  }, [columnData.length]);

  useEffect(() => {
    const hot = hotRef.current.hotInstance;

    if (
      Object.keys(auditSheetContext.selectedCell).length &&
      sheetData.length
    ) {
      const { row, col } = auditSheetContext.selectedCell;
      const index = sheetData.findIndex(d => d.id === row);
      if (index !== -1) {
        hot.selectCell(index, col);
      }
    } else {
      hot.deselectCell();
    }
  }, [auditSheetContext.selectedCell]);

  // Re-apply cell properties when comment flags change
  useEffect(() => {
    if (hotRef.current?.hotInstance) {
      hotRef.current.hotInstance.render();
    }
  }, [commentFlags]);

  useEffect(() => {
    responseAuditIssuesRef.current = responseAuditIssues;
    if (responseAuditIssues?.length && hotRef.current?.hotInstance) {
      hotRef.current.hotInstance.render();
    }
  }, [responseAuditIssues]);

  function columnHighlight(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.style.background = AppColors.lightPink;
    td.style.fontSize = getScaledRowFontSize(scale);
    td.style.textAlign = 'center';
  }

  function commentCell(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.className = `htCommentCell`;
    td.style.fontSize = getScaledRowFontSize(scale);
    td.style.textAlign = col !== RENDERING_AS_COL && 'center';
    const COL = highlightedColumn;
    if (COL.includes(col)) {
      td.style.background = AppColors.lightPink;
    }
    if (batch) {
      td.style.textAlign = 'center';
      if (agreeDisAgreeCol.includes(col)) {
        //agree
        td.innerHTML = value === true ? 'Yes' : '-';
      }
    } else {
      // td.style.textAlign = col !== RENDERING_AS_COL && 'center';

      if (agreeDisAgreeCol.includes(col)) {
        //agree
        td.innerHTML = value === true ? 'Yes' : '-';
      }
      if (dollarColumn.includes(col)) {
        //DOLLAR_COL
        td.innerHTML = value ? `${value}` : null; //$
      }
    }
  }


  const updateSheetData = (nextSheetData, row, rowIndex) => {
    if (rowIndex != null) {
      nextSheetData[rowIndex] = {
        row_id: null,
        file_name: currentUpload.upload_id,
        chart_id: currentUpload.id,
        ...nextSheetData[rowIndex],
        ...row,
      };
    } else {
      nextSheetData.push({ ...row, id: null });
    }

    return nextSheetData;
  };

  const onBeforeHotChange = change => {
    let nextSheetData = [...sheetData];
    change.map(changes => {
      const rowIndex = changes[0];
      const columnName = changes[1];
      const oldValue = changes[2];
      const newValue = changes[3];
      const isNewRow = rowIndex >= sheetData.length;

      if (newValue !== "") {
        const validationResult = validateColumnValue(columnName, newValue);
        if (!validationResult.isValid) {
          toast.showWarning({ description: validationResult.message });
          return;
        }
      }

      switch (columnName) {
        case 'srvcs_no':
          if (newValue === '0') {
            const row = {
              [columnName]: 0,
              provider_dollar_value: 0,
              provider_rvu: 0,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
            iaddState.on();
          } else if (
            Object.keys(industryCodes).length &&
            Object.keys(industryCodes).includes(newValue.split('*')[0]) &&
            validateUnits(newValue)
          ) {
            const codeKey = newValue.split('*')[0];
            const industryData = industryCodes[codeKey] || {};
            const row = {
              [columnName]: newValue,
              provider_dollar_value: industryData.par_amount,
              provider_rvu: industryData.rvu_value || 0,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
            ipddState.on();
          } else {
            toast.showError({
              description: `This Srvcs value is not available. OR 
              Please ensure that both the code and units(IF ANY) are provided in the format 'CODE*UNITS' and Units should be positive number`,
            });
            const row = {
              srvcs_no: null,
              provider_dollar_value: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
          }
          break;
        case 'audited_code':
          if (newValue === '0') {
            const row = {
              [columnName]: newValue,
              audited_dollar_value: 0,
              audited_rvu: 0,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
            iaddState.on();
          } else if (
            Object.keys(industryCodes).length &&
            Object.keys(industryCodes).includes(newValue.split('*')[0]) &&
            validateUnits(newValue)
          ) {
            const codeKey = newValue.split('*')[0];
            const industryData = industryCodes[codeKey] || {};
            const row = {
              [columnName]: newValue,
              audited_dollar_value: industryData.par_amount,
              audited_rvu: industryData.rvu_value || 0,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
            iaddState.on();
          } else {
            toast.showError({
              description: `This Audited Code is not available. OR 
              Please ensure that both the code and units(IF ANY) are provided in the format 'CODE*UNITS' and Units should be positive number`,
            });
            const row = {
              audited_code: null,
              audited_dollar_value: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
          }
          break;
        case 'agree':
          const row = {
            ...nextSheetData[changes[0]],
            [changes[1]]:
              changes[3] === ''
                ? null
                : changes[3].toLowerCase() === 'yes' ||
                  changes[3].toLowerCase() === 'true'
                ? true
                : false,
            disagree:
              changes[3] === ''
                ? null
                : changes[3].toLowerCase() === 'yes' ||
                  changes[3].toLowerCase() === 'true'
                ? false
                : true,
          };
          nextSheetData = updateSheetData(
            nextSheetData,
            row,
            isNewRow ? null : changes[0]
          );
          if (
            changes[3].toLowerCase() === 'yes' ||
            changes[3].toLowerCase() === 'true'
          ) {
            nextSheetData[changes[0]].audited_code =
              nextSheetData[changes[0]].srvcs_no;
            nextSheetData[changes[0]].audited_rvu =
              nextSheetData[changes[0]].provider_rvu;
            nextSheetData[changes[0]].audited_dollar_value =
              nextSheetData[changes[0]].provider_dollar_value;
          }
          break;
        case 'disagree':
          const disagreeRow = {
            [changes[1]]:
              changes[3] === ''
                ? null
                : changes[3].toLowerCase() === 'yes' ||
                  changes[3].toLowerCase() === 'true'
                ? true
                : false,
            agree:
              changes[3] === ''
                ? null
                : changes[3].toLowerCase() === 'yes' ||
                  changes[3].toLowerCase() === 'true'
                ? false
                : true,
          };
          nextSheetData = updateSheetData(
            nextSheetData,
            disagreeRow,
            isNewRow ? null : changes[0]
          );
          break;
        case 'response': {
          const responseRow = { response: changes[3] };
          nextSheetData = updateSheetData(nextSheetData, responseRow, isNewRow ? null : changes[0]);
          break;
        }
        case 'icd_agree':
        case 'icd_disagree':
          let editRow = handleAgreeDisagree(nextSheetData, changes);
          nextSheetData = updateSheetData(
            nextSheetData,
            editRow,
            isNewRow ? null : changes[0]
          );
          nextSheetData = updateICDAudit(nextSheetData, changes, editRow);
          break;
        case 'enc_dt':
          let data = changes[3];
          if (data) {
            const d = Date.parse(data);
            if (d) {
              const encDtRow1 = {
                [changes[1]]: format(new Date(d), 'MM-dd-yyyy'),
              };
              nextSheetData = updateSheetData(
                nextSheetData,
                encDtRow1,
                isNewRow ? null : changes[0]
              );
            } else {
              toast.showWarning({ description: 'Invalid Date: Use MM-DD-YY/YYYY format' });
            }
          } else {
            const encDtRow1 = {
              [changes[1]]: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              encDtRow1,
              isNewRow ? null : changes[0]
            );
          }

          break;
        default:
          const defaultRow = {
            [changes[1]]: changes[3] === '' ? null : changes[3],
          };
          nextSheetData = updateSheetData(
            nextSheetData,
            defaultRow,
            isNewRow ? null : changes[0]
          );
          break;
      }
    });
    setLocalSheetData(nextSheetData);
    //update audit sheet on first cell update in a row
    if (change.some(d => !sheetData[d[0]].id)) {
      updateAuditSheet({ ...sheet, data: nextSheetData });
    } else {
      updateSheet(nextSheetData);
    }
    return false;
  };

  const agreeValidator = (value, callback) => {
    setTimeout(() => {
      if (value.toLowerCase() === 'yes' || value === '-') {
        callback(true);
      } else {
        callback(false);
      }
    }, 1000);
  };

  const hasComment = (row, col) => {
    if (row < sheetData?.length) {
      const colData = batch
        ? auditSheetColumnsKeys[col]
        : auditSheetColumnsKeys[col + 1];
      const rowId = sheetData[row]?.id;
      if (rowId && commentFlags && Object.keys(commentFlags).length) {
        if (commentFlags[rowId]?.[colData]) {
          return true;
        }
      }
    }
    return false;
  };


  function columnHeaders() {
    // auditSheetHeaders

    /*
    AUDIT_SHEET_HEADER.map((header, i) => {
      const fontSize = getScaledHeaderFontSize(scale);
      return `<div style="font-size: ${fontSize};"class="header">${header}</div>`;
    }),
    
    */
    return auditSheetHeaders.map((header, i) => {
      const fontSize = getScaledHeaderFontSize(scale);
      return `<div style="font-size: ${fontSize};"class="header">${header}</div>`;
    });
  }

  function contextOption() {
    return {
      async callback(key, selection, clickEvent) {
        if (key !== 'rebuttals') return;
        const rowIndex = selection[0]?.start?.row;
        const rowData = rowIndex != null ? sheetData[rowIndex] : undefined;
        onCommentClick(
          selection,
          { pageX: clickEvent.pageX || clickEvent.clientX, pageY: clickEvent.pageY || clickEvent.clientY },
          true,
          true,
          columnData,
          rowData
        );
        const { row, uploadIds } = getRowColArray(selection[0], { ...sheet, data: sheetData }, batch);
        await getComments({ batchId, uploadId: uploadId || uploadIds[0], row });
      },
      items: {

        enable_missing_info: {
          name: '<span style="background-color: #ffe6e6; padding: 1px; border-radius: 3px;">Enable Missing Info</span>',
          callback: async function (key, selection) {
            let nextSheetData = [...sheetData];
            const temp = getRowColIndex(selection[0], sheet);
            const row = temp.rows[0];
            if (row >= 0 && row < nextSheetData.length) {
              if (nextSheetData[row].missing_info === null || nextSheetData[row].id === null) {
                nextSheetData[row].missing_info = true;
                if (nextSheetData[row].id === null) {
                  nextSheetData = updateSheetData(nextSheetData, {}, row)
                }
                await updateAuditSheet({ ...sheet, data: nextSheetData });
              } else {
                console.log("Missing Info is already true or row is empty.");
              }
            } else {
              console.log("Invalid row index.");
            }
          },
          hidden() {
            const selectedIndex = this.getSelectedLast()[0];
            return (
              selectedIndex < 0 ||
              selectedIndex >= sheetData.length ||
              sheetData[selectedIndex].missing_info === true
            );
          },
        },
        disable_missing_info: {
          name: '<span style="background-color: #e6ffe6; padding: 1px;  border-radius: 3px;">Disable Missing Info</span>',
          callback: async function (key, selection) {
            let nextSheetData = [...sheetData];
            const temp = getRowColIndex(selection[0], sheet);
            const row = temp.rows[0]
            if (row >= 0 && row < nextSheetData.length) {
              if (nextSheetData[row].missing_info !== null) {
                nextSheetData[row].missing_info = null;
                await updateAuditSheet();
              } else {
                console.log("Missing Info is already null or row is empty.");
              }
            } else {
              console.log("Invalid row index.");
            }
          },
          hidden() {
            const selectedIndex = this.getSelectedLast()[0];
            return (
              selectedIndex < 0 ||
              selectedIndex >= sheetData.length ||
              // sheetData[row].missing_info === null
              sheetData[selectedIndex].missing_info === null ||
              sheetData[selectedIndex].id === null
            );
          },
        },

        row_above: {
          name: () => '<p>Insert row above</p>',
          disabled() {
            return view;
          },
        },
        row_below: {
          disabled() {
            return view;
          },
        },
        remove_row: {
          callback: function (key, selection) {
            const { rows } = getRowColIndex(selection[0], sheet);
            removeRows(rows);
          },
          disabled() {
            return view;
          },
        },
        cut: {
          disabled() {
            return view;
          },
        },
        copy: {
          disabled() {
            return view;
          },
        },
        freeze_column: {
          name: "Freeze Column",
          disabled() {
            return !view;
          },
        },
        unfreeze_column: {
          name: "Unfreeze Column",
          disabled() {
            return !view;
          },
        },
        export_to_csv_cpt: {
          name: "Export to CSV (CPT-wise)",
          callback(key, selection, clickEvent) {
            const hot = hotRef.current.hotInstance;
    
            // Helper to escape CSV fields
            const escapeCsvField = (field) => {
                if (field === null || field === undefined) {
                    return '';
                }
                const stringField = String(field);
                if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\r\n')) {
                    const escapedField = stringField.replace(/"/g, '""');
                    return `"${escapedField}"`;
                }
                return stringField;
            };
    
            const sourceData = hot.getSourceData();

            /**
            const columnOrderProvided = [
            'file_id',
            'encounter',
            'rendering',
            'enc dt',
            'cpt',
            'modifier',
            'units',
            'ICDs',
            'Notes',
            'Response',
            'Provider Education',
            'Number & complexity',
            'Amount and complexity',
            'Mortality of Patient Management'
            ];
            */
            
            const OMITTING_KEYS = [
              'missing_info',
              'provider_rvu',
              'provider_dollar_value',
              'agree',
              'disagree',
              'audited_rvu',
              'audited_dollar_value',
              'icd_agree',
              'icd_disagree'
            ]
            const FLATTENING_KEYS = [
              'srvcs_no',
              'audited_cpt',
              'billed_modifier',
              'audited_modifier'
            ]
            

            const columnOrder = columnData.map(c => c.data);
            // these are the keys before the cpt
            const STARTING_KEYS = [
              'file_name',
              'encounter_no',
              'rendering',
              'enc_dt'
            ]
            let last_audited_icd = -1;
            let omittingIndices =[], firstFlatteningIndex = -1;
            columnOrder.forEach((val, idx) => {
              if (OMITTING_KEYS.includes(val)) {
                omittingIndices.push(idx);
                return;
              }
              for (const prefix of FLATTENING_KEYS) {
                if (val.startsWith(prefix)) {
                  omittingIndices.push(idx);
                }
              }
            });

            let filteredColumnOrder = columnOrder.filter((_, idx) => !omittingIndices.includes(idx))
            let notes_pos = -1;
            filteredColumnOrder.forEach((val, idx) => {
              if (STARTING_KEYS.includes(val)) {
                firstFlatteningIndex = idx;
              }
              if (val.startsWith("audited_icd")) {
                last_audited_icd = idx;
              }
              if (idx !== -1 && val === 'notes') {
                notes_pos = idx;
              }
            })

            firstFlatteningIndex += 1;
            
            if (last_audited_icd != -1 && notes_pos !== -1) {
              // placing the notes after the last audited icd
              filteredColumnOrder.splice(last_audited_icd + 1, 0, "notes");
              // removing the notes key from it's original position
              filteredColumnOrder.splice(notes_pos, 1);
            }
            const filteredCSVHeaders = auditSheetHeaders.filter((_, idx) => !omittingIndices.includes(idx));
            if(last_audited_icd != -1 && notes_pos !== -1) {
              // placing the Notes header after the last audited icd
              filteredCSVHeaders.splice(last_audited_icd + 1, 0, "Notes");
              // removing the old notes position in the header list
              filteredCSVHeaders.splice(notes_pos, 1);
            }

            const FLATTENED_CSV_HEADERS = ["Billed CPT", "Audited CPT", "Billed Modifier", "Audited Modifier", "Units Billed", "Units Reviewed"];
            filteredCSVHeaders.splice(firstFlatteningIndex, 0, ...FLATTENED_CSV_HEADERS)

            const processedData = sourceData.map(row => {
                const newRow = { ...row };
                if (newRow.id === null) {
                    return null; 
                }

                const responseValue = newRow.response;
                if (responseValue) {
                    newRow.response = decodeResponseValue(responseValue, row, responseAuditIssues);
                }
                return newRow;
            }).filter(Boolean); 
            
            

            
            const csvHeader = filteredCSVHeaders.map(escapeCsvField).join(',');

            const csvRows = processedData.map(row => {
              if (typeof(row) !== 'object' || row === null) {
                return '';
              }

              const createCptDict = (key, keyPrefix, dict) => {
                if (!key.startsWith(keyPrefix)) {
                  return false;
                }
                const keySplits = key.split("_")
                const cptId = parseInt(keySplits.length === 3 ? keySplits[2] : "1", 10);
                dict[cptId] = row[key];
                return true;
              }

              let flatteningKeyValue = Array.from({ length: FLATTENING_KEYS.length }, (_, i) => ({}));

              Object.keys(row).forEach(key => {
                for (const [idx, prefix] of FLATTENING_KEYS.entries()) {
                  if (createCptDict(key, prefix, flatteningKeyValue[idx])) {
                    break;
                  }
                }
                
              });

              const getCptUnits = code => {
                if (!code) {
                  return [undefined, undefined];
                }
                const cptSplits = code.split("*");
                return cptSplits.length > 1 ? [cptSplits[0], cptSplits[1]] : [cptSplits[0], 1];
              }

              const maxCPTs = Math.max(...Object.keys(flatteningKeyValue[0]), ...Object.keys(flatteningKeyValue[1]));
              let flatteningValueList = [];
              for (let idx = 1; idx <= maxCPTs; idx++) {
                if (!flatteningKeyValue[0][idx] && !flatteningKeyValue[1][idx]) {
                  continue;
                }
                
                // pushing a list of ["Billed CPT", "Audited CPT", "Billed Modifier", "Audited Modifier", "Units Billed", "Units Reviewed"]
                let units_list = []
                flatteningValueList.push(FLATTENING_KEYS.map((_, i) => {
                  if (i === 0 || i == 1) {
                    const ret = getCptUnits(flatteningKeyValue[i][idx]);
                    units_list.push(ret[1]);
                    return ret[0];
                   }
                   let ret_list = [flatteningKeyValue[i][idx]];
                   if (i === 3) {
                    ret_list.push(...units_list)
                   }
                  return ret_list
                }).flat().map(escapeCsvField))
              }

              // parse the notes
              let commonNotes = "";
              let CPTtoNotesMap = {}


              flatteningValueList.forEach(el => {
                if (el[0]) {
                  CPTtoNotesMap[el[0]] = "";
                }
              });
              const NOTES_DELIMETERS = ['.', ';']
              if (row.notes) {
                for (const note of row.notes.split(new RegExp(`[${NOTES_DELIMETERS.join('')}]`))) {
                  if (!note) {
                    continue;
                  }
                  const tokens = note.split(" ");
                  let matchedCPTs = new Set();
                  for (const token of tokens) {
                    if (token in CPTtoNotesMap) {
                      matchedCPTs.add(token);
                    }
                  }

                  for (const cpt of matchedCPTs) {
                    CPTtoNotesMap[cpt] += `${note}. `;
                  }
                  if (!matchedCPTs.size) {
                    commonNotes += `${note}. `;
                  }
                }
              }
              
              // note parsing ends

              let result = [];

              for (const flattenedItem of flatteningValueList) {
                result.push(filteredColumnOrder.map((key, idx) => {
                  let flatteningPrefix = [];
                  if (idx === firstFlatteningIndex) {
                    flatteningPrefix = flattenedItem;
                  }
                  let ret; 
                  if (key === "enc_dt") {
                    ret = `="${escapeCsvField(row['enc_dt'])}"`;
                  }
                  else if (key === "notes") {
                    if (flattenedItem[0]) {
                      ret = escapeCsvField(CPTtoNotesMap[flattenedItem[0]] + commonNotes);
                    } else {
                      ret = escapeCsvField(commonNotes);
                    }
                  }
                  else {
                    ret = escapeCsvField(row[key]);
                  }
                  return [...flatteningPrefix, ret];
                }).flat().join(','))
              }
                
              return result;
            }).flat();
    
            const csvString = [csvHeader, ...csvRows].join('\r\n');
    
            const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            if (link.download !== undefined) { 
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `audit_export.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
          },
        },
        
        export_to_csv: {
          name: "Export to CSV",
          callback(key, selection, clickEvent) {
              const hot = hotRef.current.hotInstance;
              // Helper to escape CSV fields
              const escapeCsvField = (field) => {
                if (field === null || field === undefined) {
                    return '';
                }
                const stringField = String(field);
                if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\r\n')) {
                    const escapedField = stringField.replace(/"/g, '""');
                    return `"${escapedField}"`;
                }
                return stringField;
              };
    
              const sourceData = hot.getSourceData();
              const headers = auditSheetHeaders;
              const columnOrder = columnData.map(c => c.data);
      
              const processedData = sourceData.map(row => {
                  const newRow = { ...row };
                  if (newRow.id === null) {
                      return null; 
                  }
                  const responseValue = newRow.response;
                  if (responseValue) {
                      newRow.response = decodeResponseValue(responseValue, row, responseAuditIssues);
                  }
                  return newRow;
              }).filter(Boolean); 
      
              const csvHeader = headers.map(escapeCsvField).join(',');
              const csvRows = processedData.map(row => {
                  return columnOrder.map(key => {
                      const value = (typeof row === 'object' && row !== null) ? row[key] : '';
                      return escapeCsvField(value);
                  }).join(',');
              });
      
              const csvString = [csvHeader, ...csvRows].join('\r\n');
      
              const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              if (link.download !== undefined) { 
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', `${currentUpload.upload_id}.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
              }   
            },
        },
        rebuttals: {
          name: 'View Rebuttal',
          hidden() {
            const [startRow, , endRow] = this.getSelectedLast();
            return startRow !== endRow || !sheetData[startRow]?.id;
          },
        },
      },
    };
  }

  // onKeyPress
  function onKeyPress(e) {
    // column number
    let col = this.getSelectedLast()[1];
    var evt = e || window.event; // IE support

    var key = evt.charCode || evt.keyCode || 0;

    // check for cut and paste
    var isClipboard = false;
    var ctrlDown = evt.ctrlKey || evt.metaKey; // Mac support

    // Check for Alt+Gr (http://en.wikipedia.org/wiki/AltGr_key)
    if (ctrlDown && evt.altKey) isClipboard = false;
    // Check for ctrl+c, v and x
    else if (ctrlDown && key === 67) isClipboard = true; // c
    else if (ctrlDown && key === 86) isClipboard = true; // v
    else if (ctrlDown && key === 88) isClipboard = true; // x
    else if (ctrlDown && evt.key === 'z') isClipboard = true;

    const isNumeric =
      NUMERIC_KEYS.includes(key) ||
      (key >= 35 && key <= 40) ||
      (key >= 48 && key <= 57) ||
      (key >= 96 && key <= 105);

    switch (col) {
      case 3:
        // Shift movement and copy-paste are not enabled for column 3 (Enc Date).
        if ((!isNumeric && !isClipboard) || e.shiftKey) {
          // prevent alpha characters
          e.stopImmediatePropagation();
          e.preventDefault();
        }
        break;
      default:
        break;
    }

    numericColumns.forEach(nc => {
      switch (col) {
        case nc:
          if ((!isNumeric && !isClipboard) || e.shiftKey) {
            // prevent alpha characters
            e.stopImmediatePropagation();
            e.preventDefault();
          }
          break;
        default:
          break;
      }
    });
  }

  // onMouseClick on cell
  function _afterOnCellMouseDown(e, p) {
    closeComment();
    if (p.col < 0) return; // row header click — ignore
    const rowData = sheetData[p.row];
    const rowId = rowData?.id;
    const selection = [
      {
        start: { row: p.row, col: p.col },
        end: { row: p.row, col: p.col },
      },
    ];
    const dimension = { pageX: e.clientX, pageY: e.clientY };
    const canRebuttal = !!rowId && p.col !== 0;
    onCommentClick(selection, dimension, false, canRebuttal, rowData);
  }

  function cellProps(row, col) {
    var cellProperties = {};

    const COL = highlightedColumn ?? [6, 14];
    if (COL.includes(col)) {
      cellProperties.renderer = columnHighlight; // uses lookup map
    }

    if (hasComment(row, col)) {
      cellProperties.renderer = commentCell;
    }

    // Check for missing_info and apply background color
    const rowData = this.instance.getSourceDataAtRow(row);
    if (rowData && rowData.missing_info) {
      cellProperties.className = 'missing-info';
    }

    // Row-wise rebuttal highlight
    const rowId = sheetData[row]?.id;
    if (rowId && commentFlags[rowId]?.['__row__'] !== undefined) {
      const base = cellProperties.className ? cellProperties.className + ' ' : '';
      cellProperties.className = base + (commentFlags[rowId]['__row__'] ? 'cq-row-rebuttal-open' : 'cq-row-rebuttal-resolved');
    }

    if(industryCodes){
      cellProperties.CPT = industryCodes;
    }

    if(iCDCodes){
      cellProperties.ICD = iCDCodes;
    }

    if(modifiers){
      cellProperties.Modifiers = modifiers;
    }

    return cellProperties;
  }

  function onPaste(data, coords) {
    const [startRow, startCol] = [coords[0].startRow, coords[0].startCol];
    const actualData = data[0];

    const rvuIndices = columnData.findIndex(col => col.data === 'provider_rvu');

    // Check if the destination column is 'enc_dt'
    if (startCol === rvuIndices) {
      // Check if any of the pasted values is not a number
      if (actualData.some(value => isNaN(+value))) {
        return false;
      }
    }

    // Continue with the paste operation for other columns or valid numeric values
    return true;
  }

  /** @type {import('handsontable/settings').GridSettings} */
  const hotSettings = {
    //sheet data
    data: sheetData,

    height: height,

    colHeaders: columnHeaders(),

    minSpareRows: 10,

    licenseKey: 'non-commercial-and-evaluation',

    //  right-click or context menu
    contextMenu: contextOption(),

    beforeChange: onBeforeHotChange,

    beforeKeyDown: onKeyPress,

    outsideClickDeselects: false,

    afterSelectionEnd: (row, col, row2, col2) => {
      if (onSelectionEnd) {
        onSelectionEnd(
          [{ start: { row, col }, end: { row: row2, col: col2 } }],
          sheetData[row]
        );
      }
    },

    afterOnCellMouseDown: _afterOnCellMouseDown,

    cells: cellProps,

    beforePaste: onPaste,
  };

  /** @type {import('handsontable/settings').ColumnSettings[]} */
  const auditSheetColumnData = [
    {
      data: 'encounter_no',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'rendering',
      type: 'audit_sheet.dropdown_renderer',
      width: windowSize * 0.07,
      source: providerOptions.map(p => p.value),
      readOnly: true,
    },
    {
      data: 'enc_dt',
      type: 'date',
      dateFormat: 'MM-DD-YYYY',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
      datePickerConfig: {
        // First day of the week (0: Sunday, 1: Monday, etc)
        disableDayFn(date) {
          // Disable Sunday and Saturday
          return date > new Date();
        },
      },
    },
    {
      data: 'srvcs_no',
      type: 'text',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'provider_rvu',
      type: 'numeric',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'provider_dollar_value',
      type: 'audit_sheet.currency_renderer',
      width: windowSize * 0.07,
      readOnly: view || isProviderDollarDisabled,
    },
    {
      data: 'response',
      type: 'text',
      width: windowSize * 0.15,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'agree',
      type: 'text',
      validator: agreeValidator,
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'agreeRenderer',
    },
    {
      data: 'disagree',
      type: 'text',
      validator: agreeValidator,
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'agreeRenderer',
    },
    {
      data: 'audited_code',
      type: 'text',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'audited_rvu',
      type: 'numeric',
      width: windowSize * 0.07,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
    {
      data: 'audited_dollar_value',
      width: windowSize * 0.07,
      type: 'audit_sheet.currency_renderer',
      readOnly: view || isAuditedDollarDisabled,
    },
    {
      data: 'notes',
      type: 'text',
      width: windowSize * 0.15,
      readOnly: view,
      renderer: 'customStylesRenderer',
    },
  ];

  const hotTableDefaultSettings = {
    ...hotSettings,
    rowHeights: getScaledRowHeight(scale),
    stretchH: 'all',
    autoColumnSize: false,
    autoRowSize: false,
    wordWrap: true,
  };

  return (
    <HotTable
      ref={hotRef}
      columns={columnData}//auditSheetColumnData
      data={sheetData}
      settings={{
        ...hotTableDefaultSettings,
        manualColumnResize: true,
        rowHeaders: true,
        manualColumnFreeze: true,
      }}
    ></HotTable>
  );
};
export default AuditSheet;
